import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Trash2, Pencil, Mail, ExternalLink } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import { tipoTemplateOk } from '../../utils/mansioni';

const statoContrattoInfo = (stato) => ({
  GENERATO: { color:'#888',    label:'Generato' },
  INVIATO:  { color:'#F59E0B', label:'Inviato'  },
  FIRMATO:  { color:'#22C55E', label:'Firmato'  },
}[stato] || { color:'#888', label:stato });

export default function ContrattiDipendenti() {
  const { user } = useAuth();
  const puoCreare    = puoFare(user, 'dipendenti', 'create');
  const puoEditare   = puoFare(user, 'dipendenti', 'edit');
  const puoEliminare = puoFare(user, 'dipendenti', 'delete');
  const navigate = useNavigate();
  const anno = new Date().getFullYear();

  const [contratti, setContratti]     = useState([]);
  const [templates, setTemplates]     = useState([]);
  const [dipendenti, setDipendenti]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [inviandoId, setInviandoId]   = useState(null);

  const [filtroTesto, setFiltroTesto] = useState('');
  const [filtroStato, setFiltroStato] = useState('TUTTI');

  const [dipendenteSel, setDipendenteSel] = useState('');
  const [templateSel, setTemplateSel]     = useState('');
  const [annoContratto, setAnnoContratto] = useState(anno);
  const [generando, setGenerando]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rc, rt, rd] = await Promise.all([
        api.get('/dipendenti/contratti/tutti'),
        api.get('/dipendenti/contratti/template'),
        api.get(`/dipendenti?anno=${anno}`),
      ]);
      setContratti(rc.data);
      setTemplates(rt.data);
      setDipendenti(rd.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [anno]);

  useEffect(() => { load(); }, [load]);

  const dipendenteScelto = dipendenti.find(d => String(d.id) === String(dipendenteSel));

  const generaContratto = async () => {
    if (!dipendenteSel) return alert('Seleziona un dipendente.');
    if (!templateSel) return alert('Seleziona un template.');
    setGenerando(true);
    try {
      await api.post(`/dipendenti/${dipendenteSel}/contratti/genera`,
        { template_id: parseInt(templateSel), anno: annoContratto },
        { responseType: 'blob' }
      );
      load();
    } catch(e) { alert('Errore generazione: ' + (e.response?.data?.error || e.message)); }
    finally { setGenerando(false); }
  };

  const scaricaContrattoGenerato = async (cId, fileName) => {
    try {
      const r = await api.get(`/dipendenti/contratti/generati/${cId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Errore download: ' + (e.response?.data?.error || e.message)); }
  };

  const eliminaContrattoGenerato = async (cId, label) => {
    if (!window.confirm(`Eliminare il contratto "${label}"?`)) return;
    try {
      await api.delete(`/dipendenti/contratti/generati/${cId}`);
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const rinominaContrattoGenerato = async (cId, nomeAttuale) => {
    const nuovo = window.prompt('Rinomina contratto:', nomeAttuale || '');
    if (nuovo === null || nuovo.trim() === '') return;
    try {
      await api.patch(`/dipendenti/contratti/generati/${cId}/nome`, { nome: nuovo.trim() });
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const inviaContrattoGenerato = async (c) => {
    const label = c.nome || c.template?.nome || 'Contratto';
    if (!c.dipendente?.email) return alert('Il dipendente non ha un indirizzo email in anagrafica: aggiungilo nella sua scheda prima di inviare.');
    if (!window.confirm(`Inviare via email il contratto "${label}" a ${c.dipendente.email}?`)) return;
    setInviandoId(c.id);
    try {
      await api.post(`/dipendenti/contratti/generati/${c.id}/invia`);
      load();
    } catch(e) { alert('Errore invio: ' + (e.response?.data?.error || e.message)); }
    finally { setInviandoId(null); }
  };

  const contrattiFiltrati = contratti.filter(c => {
    if (filtroStato !== 'TUTTI' && (c.stato || 'GENERATO') !== filtroStato) return false;
    if (filtroTesto.trim()) {
      const nome = `${c.dipendente?.cognome || ''} ${c.dipendente?.nome || ''}`.toLowerCase();
      if (!nome.includes(filtroTesto.trim().toLowerCase())) return false;
    }
    return true;
  });

  if (loading) return <div style={{ padding:'40px', color:'#555' }}>Caricamento...</div>;

  return (
    <div>
      <div style={{ marginBottom:'24px' }}>
        <h1 style={s.title}>Contratti — vista d'insieme</h1>
        <p style={s.sub}>Tutti i contratti di tutti i dipendenti in un'unica pagina.</p>
      </div>

      {/* Genera nuovo */}
      {puoCreare && <div style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'12px', padding:'16px', marginBottom:'20px' }}>
        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'12px' }}>GENERA CONTRATTO</div>
        {templates.length === 0 ? (
          <p style={{ fontSize: 'var(--font-size-xs)', color:'#888' }}>
            Nessun template disponibile. Carica un template .docx dalla sezione <strong style={{ color: 'var(--text)' }}>Dipendenti → Template</strong>.
          </p>
        ) : (
          <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
            <div style={{ flex:2, minWidth:'180px' }}>
              <label style={s.label}>DIPENDENTE</label>
              <select style={s.inp} value={dipendenteSel} onChange={e=>setDipendenteSel(e.target.value)}>
                <option value="">— seleziona —</option>
                {dipendenti.map(d => <option key={d.id} value={d.id}>{d.cognome} {d.nome}</option>)}
              </select>
            </div>
            <div style={{ flex:2, minWidth:'160px' }}>
              <label style={s.label}>TEMPLATE</label>
              <select style={s.inp} value={templateSel} onChange={e=>setTemplateSel(e.target.value)}>
                <option value="">— seleziona —</option>
                {templates
                  .filter(t => !dipendenteSel || tipoTemplateOk(t, dipendenteScelto?.modalita_compenso))
                  .map(t => <option key={t.id} value={t.id}>{t.nome} ({t.anno})</option>)}
              </select>
            </div>
            <div style={{ flex:1, minWidth:'90px' }}>
              <label style={s.label}>ANNO</label>
              <input style={s.inp} type="number" value={annoContratto} onChange={e=>setAnnoContratto(+e.target.value)} min={2020} max={2040}/>
            </div>
            <button style={s.btnPri} onClick={generaContratto} disabled={generando}>
              <FileText size={13}/> {generando ? 'GENERAZIONE...' : 'GENERA'}
            </button>
          </div>
        )}
      </div>}

      {/* Filtri */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap' }}>
        <input style={{ ...s.inp, maxWidth:'260px' }} placeholder="Cerca per dipendente..." value={filtroTesto} onChange={e=>setFiltroTesto(e.target.value)}/>
        <select style={s.filterSel} value={filtroStato} onChange={e=>setFiltroStato(e.target.value)}>
          <option value="TUTTI">Tutti gli stati</option>
          <option value="GENERATO">Generato</option>
          <option value="INVIATO">Inviato</option>
          <option value="FIRMATO">Firmato</option>
        </select>
      </div>

      {/* Elenco */}
      <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px' }}>
        CONTRATTI ({contrattiFiltrati.length})
      </div>
      {contrattiFiltrati.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px', color:'#555', fontSize: 'var(--font-size-sm)' }}>Nessun contratto trovato.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          {contrattiFiltrati.map(c => {
            const label = c.nome || c.template?.nome || 'Contratto';
            const si = statoContrattoInfo(c.stato || 'GENERATO');
            return (
              <div key={c.id} className="riga-scheda" style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                <FileText size={15} color="#555"/>
                <div className="riga-info" style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize: 'var(--font-size-sm)', fontWeight:'700', display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                    <button
                      style={{ background:'transparent', border:'none', color:'var(--text,#fff)', fontWeight:'700', fontSize: 'var(--font-size-sm)', cursor:'pointer', padding:0, textDecoration:'underline', display:'inline-flex', alignItems:'center', gap:'4px' }}
                      title="Apri scheda dipendente"
                      onClick={() => navigate(`/dipendenti/${c.dipendente?.id}`)}>
                      {c.dipendente?.cognome} {c.dipendente?.nome} <ExternalLink size={11}/>
                    </button>
                    {c.dipendente?.attivo === false && <span style={{ fontSize:'10px', color:'#EF4444' }}>(disabilitato)</span>}
                    <span style={{ color:'var(--text-dim)' }}>· {label}</span>
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>
                    Anno {c.anno} · {new Date(c.created_at).toLocaleDateString('it-IT')}
                  </div>
                  <div style={{ marginTop:'4px', display:'flex', alignItems:'center', gap:'6px' }}>
                    <span style={{ fontSize:'10px', fontWeight:'700', letterSpacing:'0.5px', color:si.color, background:`${si.color}15`, padding:'2px 8px', borderRadius:'6px' }}>{si.label.toUpperCase()}</span>
                    {c.stato === 'INVIATO' && c.inviato_at && <span style={{ fontSize:'10px', color:'#555' }}>il {new Date(c.inviato_at).toLocaleDateString('it-IT')}</span>}
                    {c.stato === 'FIRMATO' && c.firmato_at && <span style={{ fontSize:'10px', color:'#555' }}>il {new Date(c.firmato_at).toLocaleDateString('it-IT')}</span>}
                  </div>
                </div>
                {puoEditare && <button style={s.iconBtn} title="Rinomina" onClick={() => rinominaContrattoGenerato(c.id, c.nome)}>
                  <Pencil size={13}/>
                </button>}
                {puoEditare && <button style={s.iconBtn} title={c.dipendente?.email ? `Invia via email a ${c.dipendente.email}` : 'Nessuna email in anagrafica'}
                  disabled={inviandoId === c.id} onClick={() => inviaContrattoGenerato(c)}>
                  <Mail size={13}/>
                </button>}
                <button style={s.btnSec} onClick={() => scaricaContrattoGenerato(c.id, c.file_nome)}>
                  <Download size={13}/> Scarica
                </button>
                {puoEliminare && <button style={{ ...s.btnSec, color:'#EF4444', borderColor:'#EF444430' }} title="Elimina" onClick={() => eliminaContrattoGenerato(c.id, label)}>
                  <Trash2 size={13}/>
                </button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  title:     { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub:       { color:'var(--text-muted,#888)', fontSize: 'var(--font-size-sm)', marginTop:'2px' },
  btnPri:    { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSec:    { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input,#1A1A1A)', color:'var(--text,#fff)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  label:     { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'#555', display:'block', marginBottom:'4px' },
  inp:       { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
  filterSel: { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-xs)', outline:'none' },
  iconBtn:   { background:'transparent', border:'none', cursor:'pointer', padding:'4px', color:'var(--text-muted,#888)', display:'inline-flex', alignItems:'center' },
};
